<template>
  <v-container fluid class="pa-0 h-screen home-screen">
    <v-row no-gutters class="h-100">
      <!-- Left Side - White -->
      <v-col cols="12" md="6" class="bg-white d-flex home-left">
        <v-container class="d-flex flex-column align-center justify-center h-100">
          <v-row class="w-100 justify-center align-center">
            <v-col cols="12" class="text-center">
              <v-img width="280" class="home-logo-img mx-auto"
                :src="logo.full" alt="Cliqforms" contain />
              <div class="d-flex flex-wrap justify-center gap-3 mt-6">
                <v-row class="w-100 justify-center">
                  <v-col cols="12" sm="8" md="10" class="d-flex flex-wrap justify-center gc-3">
                    <v-btn size="large" v-if="!isLoggedIn" color="brand-color" prepend-icon="mdi-account-plus"
                      class="text-none font-weight-bold" @click="$router.push({ name: 'register' })">
                      Create Account
                    </v-btn>
                    <v-btn size="large" v-if="!isLoggedIn" color="brand-color" prepend-icon="mdi-login"
                      variant="outlined" class="text-none font-weight-bold" @click="$router.push({ name: 'login' })">
                      Sign In
                    </v-btn>
                    <v-btn size="large" v-else color="brand-color" class="text-none font-weight-bold"
                      append-icon="mdi-arrow-right" @click="$router.push({ name: 'dashboard' })">
                      Go to Dashboard
                    </v-btn>
                  </v-col>
                </v-row>
              </div>

              <v-btn variant="text" color="brand-color" prepend-icon="mdi-play-circle-outline"
                class="text-none font-weight-bold mt-4" @click="dialog.video = true">
                Watch a quick demo
              </v-btn>
            </v-col>
          </v-row>

          <v-dialog v-model="dialog.video" max-width="720" eager>
            <v-card rounded="lg">
              <video-embed :src="video.url" :poster="video.poster" />
            </v-card>
          </v-dialog>
        </v-container>
      </v-col>

      <!-- Right Side - Blue -->
      <v-col cols="12" md="6"
        class="bg-brand-color-darker home-right position-relative overflow-hidden d-flex align-center">
        <v-container class="d-flex flex-column py-0 w-90 mx-auto">
          <!-- Top Feature Card -->
          <v-row no-gutters class="w-100 mb-4 justify-center">
            <v-col cols="12" class="pa-2">
              <v-card class="feature-card feature-card-top text-white w-100" variant="outlined" rounded="lg">
                <template #prepend>
                  <v-avatar rounded="lg" color="brand-color" variant="tonal" size="40">
                    <v-icon color="white">mdi-lightning-bolt</v-icon>
                  </v-avatar>
                </template>
                <template #title>
                  <span class="font-weight-bold text-white card-title">
                    Collect information and act on it — all in one place
                  </span>
                </template>
                <template #subtitle>
                  <span class="text-white card-subtitle">
                    Build, collect, sign, and convert without switching tools.
                  </span>
                </template>
              </v-card>
            </v-col>
          </v-row>

          <!-- Features Grid -->
          <v-row no-gutters class="w-100 mb-4 justify-center">
            <v-col v-for="feature in features" :key="feature.title" cols="12" sm="6" md="6" class="pa-2">
              <v-card class="feature-card h-100 text-white" variant="outlined" rounded="lg">
                <v-card-text class="d-flex flex-column">
                  <v-avatar rounded="lg" color="brand-color" variant="tonal" size="35" class="mb-4">
                    <v-icon color="white" size="24">{{ feature.icon }}</v-icon>
                  </v-avatar>
                  <v-card-title class="text-white font-weight-bold pa-0 mb-2 card-title">
                    {{ feature.title }}
                  </v-card-title>
                  <v-card-text class="text-white pa-0 card-subtitle">
                    {{ feature.description }}
                  </v-card-text>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Bottom Mission Card -->
          <v-row no-gutters class="w-100 justify-center">
            <v-col cols="12" class="pa-2">
              <v-card class="feature-card feature-card-bottom text-white w-100" variant="outlined" rounded="lg">
                <template #prepend>
                  <v-avatar rounded="lg" color="brand-color" variant="tonal" size="40">
                    <v-icon color="white">mdi-laser-pointer</v-icon>
                  </v-avatar>
                </template>
                <template #title>
                  <span class="font-weight-bold text-white card-title">Easy to use. Easy to choose. Built
                    for real businesses.</span>
                </template>
                <template #subtitle>
                  <span class="text-white card-subtitle">
                    CliqForm's mission is to make it easy for businesses to connect with their clients,
                    understand their needs, and take meaningful action — all within one simple platform.
                  </span>
                </template>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import logoSrc from '@/assets/icons/branding/light/logo-new.png'
import videoPosterSrc from '@/assets/images/auth/video-bg.jpg'
import { helpArticles } from '@/shared/constants/help-articles'
import VideoEmbed from '@/components/common/VideoEmbed.vue'

export default {
  name: 'HomeScreen',
  components: { VideoEmbed },
  data() {
    return {
      logo: {
        full: logoSrc
      },
      video: {
        url: helpArticles.FEATURES_TOUR_GUIDDE_EMBED,
        poster: videoPosterSrc
      },
      dialog: {
        video: false
      },
      features: [
        {
          icon: 'mdi-brain',
          title: 'Smart Forms',
          description: 'Easily collect only what matters with flexible, logic-driven forms.'
        },
        {
          icon: 'mdi-view-day',
          title: 'Landing Page Builder',
          description: 'Create high-converting pages without code or complexity.'
        },
        {
          icon: 'mdi-qrcode',
          title: 'QR Code Generator',
          description: 'Scan, submit, and capture leads anywhere.'
        },
        {
          icon: 'mdi-file-sign',
          title: 'eSignatures',
          description: 'Get documents signed instantly, securely, and legally.'
        }
      ]
    }
  },
  computed: {
    ...mapGetters(['isLoggedIn'])
  }
}
</script>
